import { Grid, Stack } from "@mui/material";
import { CardTypeRatioSlider } from "Components/Merchant/FeeCalculator/CardTypeRatioSlider";
import { CostsChart } from "Components/Merchant/FeeCalculator/CostsChart/CostsChart";
import { FeeSettingWidget } from "Components/Merchant/FeeCalculator/FeeSettingsWidget";
import { ParameterWidget } from "Components/Merchant/FeeCalculator/ParameterWidget";
import { CardRatesTable } from "Components/Merchant/FeeCalculator/RatesTable";
import { YearlySavingsWidget } from "Components/Merchant/FeeCalculator/YearlySavingsWidget/YearlySavingsWidget";
import {
  CARD_FEES,
  CATEGORY_TO_PERCENTAGE_MAP,
  type CardTypeCategory,
} from "Components/Merchant/FeeCalculator/constants";
import type { CalculatorFormValues } from "Components/Merchant/FeeCalculator/schema";
import {
  calculateYearlyCosts,
  getAverageCardFees,
} from "Components/Merchant/FeeCalculator/utils";
import { Resources, useResource } from "Translations/Resources";
import { useState } from "react";
import { useForm } from "react-hook-form";

const NO_FIXED_TRANSACTION_COST = 0;

export const Calculator = () => {
  const [toggleValue, setToggleValue] =
    useState<CardTypeCategory>("mostly_personal");
  const [percentageOfPersonalCards, setPercentageOfPersonalCards] = useState(
    toggleValue ? CATEGORY_TO_PERCENTAGE_MAP[toggleValue] : 50,
  );
  const { t } = useResource();

  const { control, watch } = useForm<CalculatorFormValues>({
    defaultValues: {
      currentProviderFee: 1.2,
      currentTransactionCost: 0.25,
      suggestedProviderFee: 0.87,
      suggestedTransactionCost: 0.25,
      monthlyRevenue: 120_000,
      averageTransactionValue: 500,
      mooneDirectPayFee: 0.78,
      mooneDirectPayShare: 30,
    },
  });

  const currentProviderFee = watch("currentProviderFee");
  const currentTransactionCost = watch("currentTransactionCost");
  const suggestedProviderFee = watch("suggestedProviderFee");
  const suggestedTransactionCost = watch("suggestedTransactionCost");
  const monthlyRevenue = watch("monthlyRevenue");
  const averageTransactionValue = watch("averageTransactionValue");
  const mooneDirectPayFee = watch("mooneDirectPayFee");
  const mooneDirectPayShare = watch("mooneDirectPayShare");

  const { averageCurrentCardFee, averageSuggestedCardFee } = getAverageCardFees(
    {
      cardFees: CARD_FEES,
      currentProviderFee,
      suggestedProviderFee,
      percentageOfPersonalCards,
    },
  );

  const numberOfTransactionsPerMonth = averageTransactionValue
    ? monthlyRevenue / averageTransactionValue
    : 0;
  const numberOfTransactionsPerYear = numberOfTransactionsPerMonth * 12;

  const currentCosts = calculateYearlyCosts({
    fee: averageCurrentCardFee,
    fixedTransactionCost: currentTransactionCost,
    averageTransactionValue,
    numberOfTransactionsPerYear,
  });

  const legioneCosts = calculateYearlyCosts({
    fee: averageSuggestedCardFee,
    fixedTransactionCost: suggestedTransactionCost,
    averageTransactionValue,
    numberOfTransactionsPerYear,
  });

  const mooneCosts = calculateYearlyCosts({
    fee: mooneDirectPayFee,
    fixedTransactionCost: NO_FIXED_TRANSACTION_COST,
    averageTransactionValue,
    numberOfTransactionsPerYear,
  });

  const legioneCombinedWithMooneCosts =
    (legioneCosts * (100 - mooneDirectPayShare) +
      mooneCosts * mooneDirectPayShare) /
    100;

  return (
    <Stack gap={4} mb={40}>
      <CardRatesTable
        currentAcquiringFee={currentProviderFee}
        proposedAcquiringFee={suggestedProviderFee}
      />
      <Grid container spacing={8} mb={1}>
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}>
          <FeeSettingWidget control={control} type="current" />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}>
          <FeeSettingWidget control={control} type="proposed" />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 4 }}>
          <FeeSettingWidget control={control} type="moone" />
        </Grid>
      </Grid>
      <CardTypeRatioSlider
        value={percentageOfPersonalCards}
        toggleValue={toggleValue}
        onChange={(percentage) => setPercentageOfPersonalCards(percentage)}
        onToggleValueChange={(toggleValue) => setToggleValue(toggleValue)}
      />
      <Grid container spacing={8} mb={1}>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <ParameterWidget
            control={control}
            label={t(
              Resources.Pages.Content.MerchantFeeCalculator.MonthlyTurnover,
            )}
            name="monthlyRevenue"
            min={0}
            max={250_000}
            step={1000}
            inputEndAdornment="Kč"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <ParameterWidget
            control={control}
            label={t(
              Resources.Pages.Content.MerchantFeeCalculator
                .AverageTransactionValue,
            )}
            name="averageTransactionValue"
            min={0}
            max={10_000}
            step={25}
            inputEndAdornment="Kč"
          />
        </Grid>
      </Grid>
      <Grid container spacing={8} mb={1}>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <ParameterWidget
            control={control}
            label={t(
              Resources.Pages.Content.MerchantFeeCalculator.MooneDirectPayShare,
            )}
            name="mooneDirectPayShare"
            min={0}
            max={100}
            step={1}
            inputEndAdornment="%"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <YearlySavingsWidget
            currentCosts={currentCosts}
            legioneCosts={legioneCosts}
            mooneCosts={legioneCombinedWithMooneCosts}
          />
        </Grid>
      </Grid>
      <CostsChart
        currentCosts={currentCosts}
        legioneCosts={legioneCosts}
        mooneCosts={legioneCombinedWithMooneCosts}
        currentFee={`${averageCurrentCardFee.toFixed(3)}% + ${currentTransactionCost.toFixed(2)}Kč`}
        legioneFee={`${averageSuggestedCardFee.toFixed(3)}% + ${suggestedTransactionCost.toFixed(2)}Kč`}
        mooneFee={`${mooneDirectPayFee.toFixed(3)}%`}
      />
    </Stack>
  );
};
